import { NextSeo } from "next-seo";

import Breadcrumbs from "@/components/seo/Breadcrumbs";
import JsonLd from "@/components/seo/JsonLd";
import LanguageCard from "@/components/languages/LanguageCard";
import { HubHero } from "@/components/marketing/HubGrid";
import FAQ from "@/components/shared/FAQ";
import Footer from "@/components/shared/Footer";
import { LANGUAGE_COUNT, LANGUAGE_PAGES, languagePath } from "@/lib/languages/content";
import { breadcrumbsForPath } from "@/lib/seo/breadcrumbs";
import { hubGraph } from "@/lib/seo/graph";
import { HUBS } from "@/lib/seo/pages";

const PATH = HUBS.language.path;

const SEO_TITLE = `Auto Captions in ${LANGUAGE_COUNT} Languages | Crayo`;
const SEO_DESCRIPTION = `Crayo transcribes speech in ${LANGUAGE_COUNT} languages and burns animated captions into clips from podcasts, streams and interviews for TikTok, Reels and Shorts.`;

const FAQS = [
  {
    question: "Which languages does Crayo caption?",
    answer: `Crayo transcribes ${LANGUAGE_COUNT} spoken languages, from English, Spanish and Portuguese to Hindi, Japanese, Arabic and Vietnamese. Every language on this page has its own guide covering the script, the platforms where that audience lives and tips for captioning clips in it.`,
  },
  {
    question: "Do I have to pick the language before clipping?",
    answer:
      "You can leave the selector on auto detect, but picking the language is more reliable, especially for closely related pairs such as Czech and Slovak, Ukrainian and Russian, or Malay and Indonesian, and for recordings that mix two languages.",
  },
  {
    question: "Can I fix mistakes in the transcript?",
    answer:
      "Yes. Every caption line appears in an editable list in the editor, so you can correct a name, a diacritic or a slang spelling in place. The timing of the line stays as it was.",
  },
  {
    question: "Can Crayo translate captions into another language?",
    answer:
      "Yes. The caption list has a Translate button that sends your lines to Crayo's translator and swaps the text into another language from the list while keeping the original timing, so one clip can go out with captions in several languages.",
  },
];

const CaptionsHub = () => {
  const crumbs = breadcrumbsForPath(PATH);
  const entries = Object.entries(LANGUAGE_PAGES);

  return (
    <>
      <NextSeo title={SEO_TITLE} description={SEO_DESCRIPTION} />
      <JsonLd
        nodes={hubGraph({
          path: PATH,
          name: `Auto captions in ${LANGUAGE_COUNT} languages`,
          description: SEO_DESCRIPTION,
          items: entries.map(([slug, language]) => ({
            name: `${language.name} auto captions`,
            url: languagePath(slug),
            description: language.seoDescription,
          })),
          faqs: FAQS,
        })}
      />

      <article className="w-full overflow-x-clip bg-white">
        <Breadcrumbs items={crumbs} />
        <HubHero
          eyebrow="Caption languages"
          title={`Auto captions in ${LANGUAGE_COUNT} languages`}
          intro="Crayo transcribes the speech in your podcast, stream or interview and burns animated, word-timed captions into every clip. Pick a language to see how its script behaves on screen, where that audience watches and how to get the cleanest captions."
        />

        <div className="mx-auto flex w-full max-w-screen-xl flex-col gap-5 px-5 py-12 sm:px-6 md:px-12 md:py-16 lg:px-20 lg:py-20">
          <div className="flex items-baseline justify-between gap-4">
            <h2 className="text-[24px] font-semibold tracking-tight text-[#101828] md:text-[28px]">
              Every supported language
            </h2>
            <span className="shrink-0 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[#8B93A7]">
              {LANGUAGE_COUNT} languages
            </span>
          </div>
          <ul className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-4">
            {entries.map(([slug, language]) => (
              <li key={slug} className="h-full">
                <LanguageCard slug={slug} language={language} />
              </li>
            ))}
          </ul>
        </div>

        <FAQ items={FAQS} />
      </article>

      <Footer />
    </>
  );
};

export default CaptionsHub;
